import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronUp, ChevronDown, Plus, Pencil, Check, X } from "lucide-react";
import { getAdminTree, addCategory, editCategory, addSubcategory, editSubcategory, reorderItems } from "./plannerApi";

const inputCls = "bg-white border border-oth-line rounded-xl px-3 py-2 font-body text-sm focus:outline-none focus:border-oth-blue transition-colors";

const InlineEdit = ({ value, onSave, testId, width = "w-44" }) => {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(value);
    if (!editing) {
        return (
            <span className="inline-flex items-center gap-1.5">
                <span className="font-medium">{value}</span>
                <button onClick={() => { setDraft(value); setEditing(true); }} className="text-oth-ink3 hover:text-oth-blue" data-testid={`${testId}-edit`}><Pencil size={12} /></button>
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-1.5">
            <input className={`${inputCls} ${width}`} value={draft} onChange={(e) => setDraft(e.target.value)} data-testid={`${testId}-input`} />
            <button onClick={() => { onSave(draft); setEditing(false); }} className="text-emerald-600" data-testid={`${testId}-save`}><Check size={14} /></button>
            <button onClick={() => setEditing(false)} className="text-oth-ink3"><X size={14} /></button>
        </span>
    );
};

const ReturnEdit = ({ value, onSave, testId }) => {
    const [draft, setDraft] = useState(value);
    useEffect(() => setDraft(value), [value]);
    return (
        <span className="inline-flex items-center gap-1">
            <input
                type="number" step="0.1"
                className={`${inputCls} w-20 text-right`}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={() => { const n = Number(draft); if (isFinite(n) && n !== value) onSave(n); }}
                data-testid={testId}
            />
            <span className="text-xs text-oth-ink3">%</span>
        </span>
    );
};

export const PlannerCategoriesAdmin = () => {
    const [tree, setTree] = useState(null);
    const [newCat, setNewCat] = useState("");
    const [newSub, setNewSub] = useState({}); // {catId: {name, ret}}

    const load = useCallback(() => getAdminTree().then((d) => setTree(d.categories)).catch(() => toast.error("Could not load categories.")), []);
    useEffect(() => { load(); }, [load]);

    const act = async (fn, ok) => { try { await fn(); ok && toast.success(ok); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Action failed."); } };

    const move = (list, kind, idx, dir) => {
        const ids = list.map((x) => x.id);
        const j = idx + dir;
        if (j < 0 || j >= ids.length) return;
        [ids[idx], ids[j]] = [ids[j], ids[idx]];
        act(() => reorderItems(kind, ids));
    };

    if (!tree) return <div className="font-editorial italic text-oth-ink3">Loading…</div>;

    return (
        <div className="space-y-6" data-testid="planner-categories-admin">
            <div className="rounded-2xl border border-oth-line/70 bg-white p-5">
                <div className="text-xs uppercase tracking-[0.22em] text-oth-ink3 font-body mb-3">Add category</div>
                <div className="flex gap-2">
                    <input className={`${inputCls} flex-1`} placeholder="New category name" value={newCat} onChange={(e) => setNewCat(e.target.value)} data-testid="new-category-name" />
                    <button onClick={() => newCat.trim() && act(() => addCategory({ name: newCat.trim() }).then(() => setNewCat("")), "Category added.")} className="btn-pill btn-pill-blue text-sm" data-testid="new-category-add"><Plus size={14} /> Add</button>
                </div>
            </div>

            {tree.map((cat, ci) => (
                <div key={cat.id} className={`rounded-2xl border bg-white p-5 ${cat.active ? "border-oth-line/70" : "border-red-200 opacity-70"}`} data-testid={`admin-cat-${cat.id}`}>
                    <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                        <div className="flex items-center gap-3">
                            <div className="flex flex-col">
                                <button onClick={() => move(tree, "category", ci, -1)} className="text-oth-ink3 hover:text-oth-ink" data-testid={`cat-up-${cat.id}`}><ChevronUp size={14} /></button>
                                <button onClick={() => move(tree, "category", ci, 1)} className="text-oth-ink3 hover:text-oth-ink" data-testid={`cat-down-${cat.id}`}><ChevronDown size={14} /></button>
                            </div>
                            <div className="font-display text-base font-semibold"><InlineEdit value={cat.name} onSave={(v) => act(() => editCategory(cat.id, { name: v }), "Renamed.")} testId={`cat-name-${cat.id}`} /></div>
                            {cat.allow_custom && <span className="text-[10px] uppercase tracking-widest bg-[#eef3f8] text-oth-blue rounded-full px-2.5 py-1 font-body">users can add custom</span>}
                            {!cat.active && <span className="text-[10px] uppercase tracking-widest bg-red-50 text-red-600 rounded-full px-2.5 py-1 font-body">inactive</span>}
                        </div>
                        <button onClick={() => act(() => editCategory(cat.id, { active: !cat.active }), cat.active ? "Deactivated (historical plans keep their data)." : "Reactivated.")} className={`text-xs font-body px-3 py-1.5 rounded-full border transition-colors ${cat.active ? "border-oth-line text-oth-ink3 hover:text-red-600 hover:border-red-300" : "border-emerald-300 text-emerald-700"}`} data-testid={`cat-toggle-${cat.id}`}>
                            {cat.active ? "Deactivate" : "Reactivate"}
                        </button>
                    </div>

                    <div className="space-y-1.5">
                        {cat.subcategories.map((sub, si) => (
                            <div key={sub.id} className={`flex items-center justify-between gap-3 rounded-xl px-3 py-2 ${sub.active ? "bg-[#fafaf8]" : "bg-red-50/50 opacity-70"}`} data-testid={`admin-sub-${sub.id}`}>
                                <div className="flex items-center gap-2 text-sm font-body min-w-0">
                                    <div className="flex flex-col shrink-0">
                                        <button onClick={() => move(cat.subcategories, "subcategory", si, -1)} className="text-oth-ink3 hover:text-oth-ink" data-testid={`sub-up-${sub.id}`}><ChevronUp size={12} /></button>
                                        <button onClick={() => move(cat.subcategories, "subcategory", si, 1)} className="text-oth-ink3 hover:text-oth-ink" data-testid={`sub-down-${sub.id}`}><ChevronDown size={12} /></button>
                                    </div>
                                    <InlineEdit value={sub.name} onSave={(v) => act(() => editSubcategory(sub.id, { name: v }), "Renamed.")} testId={`sub-name-${sub.id}`} />
                                </div>
                                <div className="flex items-center gap-3 shrink-0">
                                    <ReturnEdit value={sub.expected_return} onSave={(v) => act(() => editSubcategory(sub.id, { expected_return: v }), "Expected return updated.")} testId={`sub-return-${sub.id}`} />
                                    <button onClick={() => act(() => editSubcategory(sub.id, { active: !sub.active }), sub.active ? "Deactivated." : "Reactivated.")} className={`text-xs font-body ${sub.active ? "text-oth-ink3 hover:text-red-600" : "text-emerald-700"}`} data-testid={`sub-toggle-${sub.id}`}>
                                        {sub.active ? "Deactivate" : "Reactivate"}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="flex gap-2 mt-3">
                        <input className={`${inputCls} flex-1`} placeholder="New sub-category" value={newSub[cat.id]?.name || ""} onChange={(e) => setNewSub({ ...newSub, [cat.id]: { ...(newSub[cat.id] || {}), name: e.target.value } })} data-testid={`new-sub-name-${cat.id}`} />
                        <input type="number" step="0.1" className={`${inputCls} w-28`} placeholder="Return %" value={newSub[cat.id]?.ret ?? ""} onChange={(e) => setNewSub({ ...newSub, [cat.id]: { ...(newSub[cat.id] || {}), ret: e.target.value } })} data-testid={`new-sub-return-${cat.id}`} />
                        <button
                            onClick={() => {
                                const d = newSub[cat.id];
                                if (!d?.name?.trim()) return;
                                act(() => addSubcategory({ category_id: cat.id, name: d.name.trim(), expected_return: Number(d.ret) || 0 }).then(() => setNewSub({ ...newSub, [cat.id]: { name: "", ret: "" } })), "Sub-category added.");
                            }}
                            className="btn-pill btn-pill-dark text-xs" data-testid={`new-sub-add-${cat.id}`}
                        ><Plus size={13} /> Add</button>
                    </div>
                </div>
            ))}
            <p className="text-xs text-oth-ink3 font-body">Deactivating never deletes historical data — plans keep the return snapshot from when they were saved.</p>
        </div>
    );
};
